import { DocumentNode, NetworkStatus, gql } from '@apollo/client';
import { Button, Stack } from '@chakra-ui/react';
import dayjs from 'dayjs';
import * as React from 'react';

import { EventListItem } from '@/components/EventListItem';
import { EventListPlaceholder } from '@/components/EventListPlaceholder';
import { ListDividerWithText } from '@/imports/ui/components/ListDividerWithText';

import { EventList_EventFragment } from './graphql-types/EventList_EventFragment';

import { EventListEmpty } from './components/EventListEmpty';

export const EventList: React.FC<{
  events?: EventList_EventFragment[];
  hasMore?: boolean;
  fetchMore?: () => void;
  networkStatus: NetworkStatus;
  trackClickEvent?: (eventId: string) => void;
  kind?: string;
  empty?: React.ReactElement;
}> & { eventFragment: DocumentNode } = (props) => {
  if (props.networkStatus === NetworkStatus.loading) {
    return <EventListPlaceholder />;
  }

  if (
    props.networkStatus === NetworkStatus.setVariables &&
    !props.events?.length
  ) {
    return <EventListPlaceholder />;
  }

  if (!props.events?.length) {
    return props.empty || <EventListEmpty title="There are no events here" />;
  }

  return (
    <Stack spacing="4">
      {props.events.map((event, index, events) => {
        const currentStartDate = event.startDate;
        const previousStartDate = events[index - 1]?.startDate;
        const displayMonthAbove =
          index !== 0 &&
          events.length > 1 &&
          !!currentStartDate &&
          !!previousStartDate &&
          new Date(currentStartDate).getMonth() !==
            new Date(previousStartDate).getMonth();

        return (
          <React.Fragment key={event._id}>
            {displayMonthAbove && (
              <ListDividerWithText>
                {dayjs(currentStartDate).format('MMMM')}
              </ListDividerWithText>
            )}
            <EventListItem
              event={event}
              trackClickEvent={() =>
                props.trackClickEvent && props.trackClickEvent(event._id)
              }
            />
          </React.Fragment>
        );
      })}
      {props.hasMore && (
        <Button
          variant="link"
          colorScheme="black"
          onClick={props.fetchMore}
          isLoading={props.networkStatus === NetworkStatus.fetchMore}
        >
          View More
        </Button>
      )}
    </Stack>
  );
};

EventList.eventFragment = gql`
  fragment EventList_EventFragment on EventV2 {
    _id
    startDate
    ...EventListItem_EventFragment
  }
  ${EventListItem.eventFragment}
`;
